import React, {useEffect, useState} from "react";
import _ from "lodash"
import {addDays, format} from "date-fns"
import {Box, Typography, Chip, Button, Alert, Tooltip} from "@mui/material";
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography';
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, LIGHT_BG} from '../../styles/foundation/colors';
import {SHADOW} from '../../styles/foundation/effects';

const cardSx = {bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: SHADOW.card, p: {xs: 2, md: 2.5}};

// Change 3: human-readable courier name — "Parent - ServiceType", never the internal account code.
const getCourierDisplayName = (provider) => {
    if (!provider) return 'Courier'
    if (!provider.parent) return provider.name || 'Courier'   // fallback
    let name = provider.parent
    if (provider.service_type_primary) name += ' - ' + provider.service_type_primary
    if (provider.service_type_secondary) name += ' (' + provider.service_type_secondary + ')'
    return name
}

// EDD as a real date alongside the day count (e.g. "5 days · 5th Aug"), not days alone.
const eddDateLabel = (days) => (days != null ? format(addDays(new Date(), Number(days)), 'do MMM') : null)

// Bifurcate couriers into Surface / Air / SDD / NDD / Hyperlocal sections.
const MODE_ORDER = ['SURFACE', 'AIR', 'SDD', 'NDD', 'HYPERLOCAL']
const MODE_LABEL = {SURFACE: 'Surface', AIR: 'Air', SDD: 'Same-Day Delivery', NDD: 'Next-Day Delivery', HYPERLOCAL: 'Hyperlocal'}

// Classify a rate row into a delivery-mode bucket. SDD/NDD live in the service-type code
// (e.g. SDD_FWD_REG, INTERCITY_NDD) — NOT in dispatch_mode, which only holds AIR/SURFACE.
// So read the service type first, then fall back to dispatch_mode.
const classifyMode = (item) => {
    const p = item?.provider || {}
    const st = `${p.service_type_primary || ''} ${p.service_type_secondary || ''}`.toUpperCase()
    if (st.includes('SDD')) return 'SDD'
    if (st.includes('NDD')) return 'NDD'
    if (st.includes('HYPERLOCAL') || st.includes('LOCAL')) return 'HYPERLOCAL'
    if (st.includes('AIR')) return 'AIR'
    if (st.includes('SURFACE')) return 'SURFACE'
    const dm = (p.dispatch_mode || '').toUpperCase()
    return dm === 'AIR' || dm === 'SURFACE' ? dm : 'OTHER'
}
const modeGroupsOf = (list) => {
    const byMode = _.groupBy(list || [], classifyMode)
    const knownModes = MODE_ORDER.filter((m) => byMode[m]?.length)
    const otherModes = Object.keys(byMode).filter((m) => !MODE_ORDER.includes(m))
    return [...knownModes, ...otherModes].map((m) => ({key: m, label: MODE_LABEL[m] || m, items: byMode[m]}))
}

// De-duplicate courier partners. A single courier brand (parent) is allocated to a merchant
// under multiple account codes — each a distinct provider.id — so de-duping by id keeps them
// all and the same partner shows up several times. Instead, collapse on the business identity
// (parent + service type) and keep the CHEAPEST row per identity, so genuinely different
// services (Surface vs Air vs SDD) still show, but repeated accounts of the same courier+service
// don't. Falls back to provider.name/id only when parent/service are absent.
const dedupeCouriers = (list) => {
    const cheapestFirst = _.orderBy(list || [], (r) => Number(r?.priceAfterTax) || Infinity, 'asc')
    return _.uniqBy(cheapestFirst, (r) => {
        const p = r?.provider || {}
        const parent = (p.parent || p.name || p.id || '').toString().toLowerCase().trim()
        const primary = (p.service_type_primary || '').toString().toLowerCase().trim()
        const secondary = (p.service_type_secondary || '').toString().toLowerCase().trim()
        return `${parent}|${primary}|${secondary}`
    })
}

// When collapsed, show at most this many couriers per mode section — enough that every
// section (incl. SDD/NDD) is visible, without a huge default list. "View all" expands.
const PER_GROUP = 3;

const RateList = ({rateListing, rtoListing, withQC = false, orderType = 'FORWARD'}) => {
    const [allResults, setAllResults] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [ratesFound, setRatesFound] = useState(false);

    useEffect(() => {
        if (rateListing && rateListing[0] && rateListing[0].message === 'Zone value not found:') {
            setRatesFound(false);
            setAllResults([]);
        } else {
            setRatesFound(true)
            // De-duplicate by courier (parent + service type, cheapest kept) so the same
            // partner never shows up twice across its multiple account codes.
            setAllResults(dedupeCouriers(rateListing));
            setExpanded(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rateListing]);

    // Change 1: index RTO results by provider.id to match forward rows side-by-side.
    const rtoByProvider = _.keyBy((rtoListing || []).filter((r) => r?.provider?.id), 'provider.id')
    const rtoPriceFor = (row) => {
        const m = row?.provider?.id ? rtoByProvider[row.provider.id] : null
        return m ? m.priceAfterTax : null
    }

    return (
        <Box sx={cardSx}>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}}>Courier-wise shipping rates</Typography>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mb: 1.5}}>Sorted estimates across your allocated couriers</Typography>

            {/* Change 6: persistent GST-inclusive note */}
            {ratesFound && (
                <Alert severity="info" sx={{mb: 2, py: 0.25, fontFamily: FONT, fontSize: SIZE['2xs'], '& .MuiAlert-message': {fontSize: SIZE['2xs']}}}>
                    All prices shown are inclusive of 18% GST.
                </Alert>
            )}

            {!ratesFound && (
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 4}}>
                    <Box sx={{width: 56, height: 56, borderRadius: '50%', bgcolor: LIGHT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', color: SUBTLE, mb: 1.5, '& svg': {fontSize: 28}}}>
                        <SearchOffOutlinedIcon />
                    </Box>
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}}>No rates for this route</Typography>
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5}}>We couldn&apos;t find serviceable rates for these pincodes. Try a different route.</Typography>
                </Box>
            )}

            {/* Bifurcated by service type (Surface / Air / SDD / NDD / Hyperlocal) — each mode gets
               its own labeled section. Collapsed shows up to PER_GROUP per section; "View all" expands. */}
            {ratesFound && modeGroupsOf(allResults).map((group) => {
                const items = expanded ? group.items : group.items.slice(0, PER_GROUP)
                return (
                <Box key={group.key} sx={{mb: 2, '&:last-of-type': {mb: 0}}}>
                    <Typography sx={{fontFamily: FONT, fontSize: '10.5px', fontWeight: WEIGHT.extra, letterSpacing: '0.06em', textTransform: 'uppercase', color: SUBTLE, mb: 1}}>
                        {group.label} <Box component="span" sx={{fontWeight: WEIGHT.regular, textTransform: 'none', letterSpacing: 0}}>({group.items.length})</Box>
                    </Typography>
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'}, gap: 1.5}}>
                        {items.map((item, index) => {
                            const isForward = orderType === 'FORWARD'
                            const rtoPrice = isForward ? rtoPriceFor(item) : null
                            const showQc = withQC && (item.qcCharge != null && Number(item.qcCharge) > 0)
                            const codCharge = item.codInvoiceChargeValue != null && Number(item.codInvoiceChargeValue) > 0 ? Number(item.codInvoiceChargeValue) : null
                            const eddDate = eddDateLabel(item.providerMaxTatDays)
                            return (
                                <Box key={index} sx={{
                                    p: 1.75, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: WHITE,
                                    transition: 'box-shadow 0.2s', '&:hover': {boxShadow: SHADOW.cardHover},
                                }}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0}}>
                                        <Box sx={{width: 36, height: 36, borderRadius: '8px', bgcolor: `${PRIMARY_BLUE}12`, color: PRIMARY_BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '& svg': {fontSize: 20}}}>
                                            <LocalShippingOutlinedIcon />
                                        </Box>
                                        <Box sx={{minWidth: 0}}>
                                            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{getCourierDisplayName(item.provider)}</Typography>
                                            {item.zone && (
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 0.3, flexWrap: 'wrap'}}>
                                                    <Chip label={item.zone} size="small" variant="outlined" sx={{fontFamily: FONT, fontSize: '0.62rem', height: 18}} />
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{mt: 1.25}}>
                                        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
                                            EDD {item.providerMaxTatDays} days{eddDate ? ` · ${eddDate}` : ''}
                                        </Typography>
                                        {showQc && (
                                            <Typography sx={{fontFamily: FONT, fontSize: '0.62rem', color: SUBTLE, mt: 0.3}}>QC charge: ₹{item.qcCharge}</Typography>
                                        )}
                                    </Box>
                                    {/* Pricing breakdown */}
                                    <Box sx={{mt: 1.25, pt: 1.25, borderTop: `1px dashed ${BORDER}`}}>
                                        {isForward ? (
                                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
                                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>Forward (incl. tax)</Typography>
                                                        <Tooltip arrow title={codCharge != null ? `Includes 18% GST and COD Collection Charge: ₹${codCharge}` : 'Includes 18% GST'}>
                                                            <InfoOutlinedIcon sx={{fontSize: 13, color: SUBTLE, cursor: 'pointer'}} />
                                                        </Tooltip>
                                                    </Box>
                                                    <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: SUCCESS}}>₹{item.priceAfterTax}</Typography>
                                                </Box>
                                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                                    <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>RTO (incl. tax)</Typography>
                                                    <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: rtoPrice != null ? NAVY : SUBTLE}}>
                                                        {rtoPrice != null ? `₹${rtoPrice}` : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>Price (incl. tax)</Typography>
                                                <Typography sx={{fontFamily: FONT, fontSize: '1.05rem', fontWeight: WEIGHT.extra, color: SUCCESS}}>₹{item.priceAfterTax}</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )
                        })}
                    </Box>
                </Box>
                )
            })}

            {ratesFound && modeGroupsOf(allResults).some((g) => g.items.length > PER_GROUP) && (
                <Button onClick={() => setExpanded((e) => !e)} sx={{fontFamily: FONT, textTransform: 'none', color: PRIMARY_BLUE, p: 0, mt: 1.5}}>
                    {expanded ? 'Show less' : `View all options (${allResults.length})`}
                </Button>
            )}
        </Box>
    );
};

export default RateList;
